import { auth } from '@mxa-argos/core/config/firebase';
import { showErrorToast, showPartialDataToast } from '@mxa-argos/lib/error-toast';
import { triggerConfetti } from '@mxa-argos/lib/confetti';
import { Client, fetchExchange, mapExchange, OperationResult } from '@urql/core';
import { STAFF_GRAPHQL_URL } from '@mxa-argos/core/config/api';
import { forceLogout, isUserLoggingOut } from '@mxa-argos/modules/auth/services/force-logout';


async function getFreshToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  return user.getIdToken();
}

function isUnauthorizedError(error: {
  graphQLErrors?: Array<{ extensions?: { code?: string } }>;
}): boolean {
  return (
    error.graphQLErrors?.some((gqlError) => gqlError.extensions?.code === 'UNAUTHORIZED') ?? false
  );
}

function hasPartialData(result: OperationResult): boolean {
  if (result.operation.kind !== 'query' || result.error?.networkError || !result.data) {
    return false;
  }

  return Object.values(result.data as Record<string, unknown>).some(
    (value) => value !== null && value !== undefined,
  );
}

export interface ArgosOperationContext {
  shouldSkipErrorToast?: boolean;
}

const resultExchange = mapExchange({
  onResult(result) {
    if (result.error) {
      if (isUserLoggingOut()) return;
      if (isUnauthorizedError(result.error)) {
        forceLogout();
        return;
      }
      if (hasPartialData(result)) {
        console.error(result.error);
        showPartialDataToast(result.error);
        return { ...result, error: undefined };
      }
      const { shouldSkipErrorToast } = result.operation.context as ArgosOperationContext;
      if (shouldSkipErrorToast) return;
      showErrorToast(result.error);
    } else if (result.operation.kind === 'mutation') {
      triggerConfetti();
    }
  },
});

const authenticatedFetch: typeof fetch = async (input, init) => {
  const token = await getFreshToken();

  const headers = new Headers(init?.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(input, { ...init, headers });
};

export const graphqlClient = new Client({
  url: STAFF_GRAPHQL_URL,
  exchanges: [resultExchange, fetchExchange],
  fetch: authenticatedFetch,
  fetchOptions: () => ({
    headers: {
      'content-type': 'application/json',
      'x-apollo-operation-name': 'mxa-argos',
    },
  }),
});
